import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Copy, Loader2, Plus, Search } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { KeyValuesApi } from '@/services/targeting/targetingLookupApi'

export interface KeyValueSearchProps {
  /** Adds one value of a key to the row being edited. */
  onAddValue: (key: string, value: string) => void
  /** Adds the whole key with all its values as its own alternative. */
  onCopyRow: (key: string, values: string[]) => void
}

const PAGE_SIZE = 25

/**
 * Searches the key-values the network has uploaded as reference data, so a
 * key and its values can be picked rather than typed from memory.
 */
export function KeyValueSearch({ onAddValue, onCopyRow }: KeyValueSearchProps) {
  const { t } = useTranslation()
  const [term, setTerm] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)

  const { data, isFetching } = useQuery({
    queryKey: ['targeting-keyvalues', query, page],
    queryFn: () => KeyValuesApi.search(query, page, PAGE_SIZE),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  })

  const results = data?.results ?? []
  const totalHits = data?.totalHits ?? 0
  const search = () => {
    setPage(0)
    setQuery(term)
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">{t('targeting.keyValue.searchHelp')}</p>
      <div className="flex items-center gap-2">
        <Input
          type="search"
          value={term}
          placeholder={t('targeting.keyValue.searchPlaceholder')}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              search()
            }
          }}
        />
        <Button type="button" variant="outline" size="sm" onClick={search}>
          {isFetching ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Search className="size-4" />
          )}
          {t('common.search')}
        </Button>
      </div>

      {results.length === 0 && !isFetching && (
        <p className="text-xs text-muted-foreground">{t('targeting.noResults')}</p>
      )}

      {results.map((result) => (
        <div key={result.key} className="flex flex-col gap-2 rounded-md border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-semibold">{result.key}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onCopyRow(result.key, result.values)}
            >
              <Copy className="size-3.5" />
              {t('targeting.keyValue.copyRow')}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {result.values.map((value) => (
              <Button
                key={value}
                type="button"
                variant="secondary"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onAddValue(result.key, value)}
              >
                <Plus className="size-3" />
                {value}
              </Button>
            ))}
          </div>
        </div>
      ))}

      {totalHits > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
          <span>
            {t('targeting.keyValue.showing', {
              from: page * PAGE_SIZE + 1,
              to: Math.min((page + 1) * PAGE_SIZE, totalHits),
              total: totalHits,
            })}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('common.previous')}
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('common.next')}
            disabled={(page + 1) * PAGE_SIZE >= totalHits}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}
    </div>
  )
}
